import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Globe, Loader2, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, errMsg, money, TECH } from "../lib/api";
import { useCountry } from "../context/CountryContext";
import { AddressAutocomplete, Suggest, useListNav } from "./Autocomplete";
import { DomainResults } from "./DomainResults";
import { PackageCard } from "./PackageCard";

export const CoverageChecker = ({ onResult }) => {
  const { code } = useCountry();
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setAddress(""), [code]);
  const run = async (addr) => {
    if ((addr || "").trim().length < 3) return toast.error("Enter a street address, suburb or city");
    setBusy(true);
    try {
      const { data } = await api.post("/coverage", { country: code, address: addr });
      onResult(data);
      toast.success(data.packages.length ? `${data.packages.length} packages available at this address` : "No coverage at this address yet");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); run(address); }} className="flex flex-col gap-2 sm:flex-row">
      <AddressAutocomplete value={address} onChange={setAddress} onSelect={(it) => run(it.label)} testid="coverage-search-input"
        placeholder="Start typing your street address" className="h-12 rounded-full bg-white text-base text-foreground" />
      <Button data-testid="coverage-check-btn" className="h-12 rounded-full px-7 font-bold" disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check coverage"}
      </Button>
    </form>
  );
};

export const DomainSearch = ({ onResult, initial = "" }) => {
  const { code, country } = useCountry();
  const [q, setQ] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [tlds, setTlds] = useState({ tlds: [], free_tld: null });
  useEffect(() => { api.get("/domains/tlds", { params: { country: code } }).then((r) => setTlds(r.data)).catch(() => {}); }, [code]);
  const run = async (query) => {
    if ((query || "").trim().length < 2) return toast.error("Enter a domain name");
    setBusy(true); setOpen(false);
    try {
      const { data } = await api.get("/domains/search", { params: { q: query, country: code, has_package: false } });
      onResult(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { if (initial) run(initial); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = q.trim().toLowerCase().split(".")[0].replace(/[^a-z0-9-]/g, "");
  const items = label.length >= 2 ? tlds.tlds.map((t) => ({ ...t, domain: label + t.tld })) : [];
  const pick = (it) => { setQ(it.domain); run(it.domain); };
  const nav = useListNav(items, pick);
  return (
    <form onSubmit={(e) => { e.preventDefault(); run(q); }} className="flex flex-col gap-2 sm:flex-row">
      <div className="relative flex-1">
        <Globe className="absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-primary" />
        <Input data-testid="domain-search-input" value={q} autoComplete="off" onKeyDown={nav.onKeyDown}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)}
          placeholder="yourbusiness" className="h-12 rounded-full bg-white pl-10 font-mono-data text-base text-foreground" />
        {open && items.length > 0 && (
          <Suggest items={items} active={nav.active} onPick={pick} testid="domain-suggestions" render={(it) => (
            <>
              <Globe className="h-4 w-4 shrink-0 text-primary" />
              <span className="flex-1 font-mono-data"><b>{label}</b><span className="text-primary">{it.tld}</span></span>
              {it.tld === tlds.free_tld && <span className="rounded-full bg-[#DDF7EE] px-2 py-0.5 text-[10px] font-bold text-[#0B7A6F]">FREE with internet/hosting</span>}
              <span className="text-xs font-semibold text-muted-foreground">{money(it.price, country?.currency)}/yr</span>
            </>
          )} />
        )}
      </div>
      <Button data-testid="domain-search-btn" className="h-12 rounded-full px-7 font-bold" disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-2 h-4 w-4" />Search</>}
      </Button>
    </form>
  );
};

export const CoverageResults = ({ data }) => (
  <div data-testid="coverage-results" className="mt-8">
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <p className="text-sm text-muted-foreground">Results for <span className="font-semibold text-foreground">{data.address}</span>:</p>
      {data.technologies.map((t) => <span key={t} className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">{TECH[t]?.label} available</span>)}
      {data.simulated && <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">Partly SIMULATED (supplier API pending)</span>}
    </div>
    {data.packages.length ? (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.packages.map((p, i) => <PackageCard key={p.id} pkg={p} delay={i * 50} />)}
      </div>
    ) : (
      <p className="rounded-lg border bg-white p-6 text-sm text-muted-foreground" data-testid="coverage-none">No packages at this address yet. Try your suburb or city name.</p>
    )}
  </div>
);

export const ActionHub = ({ onCoverage, onDomains }) => (
  <Tabs defaultValue="coverage" className="w-full">
    <TabsList className="mb-3 h-auto rounded-full bg-muted p-1">
      <TabsTrigger value="coverage" data-testid="hero-tab-coverage" className="rounded-full px-4 py-2 font-bold text-foreground/70 data-[state=active]:bg-primary data-[state=active]:text-white">
        <MapPin className="mr-1.5 h-4 w-4" /> Check coverage
      </TabsTrigger>
      <TabsTrigger value="domains" data-testid="hero-tab-domains" className="rounded-full px-4 py-2 font-bold text-foreground/70 data-[state=active]:bg-primary data-[state=active]:text-white">
        <Globe className="mr-1.5 h-4 w-4" /> Find a domain
      </TabsTrigger>
    </TabsList>
    <TabsContent value="coverage"><CoverageChecker onResult={onCoverage} /></TabsContent>
    <TabsContent value="domains"><DomainSearch onResult={onDomains} /></TabsContent>
  </Tabs>
);
